import React, { useEffect, useState, useMemo } from "react";
import API from "../services/api";
import Sidebar from "../components/Sidebar";
import { 
  FiSearch, FiActivity, FiMapPin, FiImage, FiPlayCircle, 
  FiMic, FiX, FiPhone, FiMessageCircle, FiUser, FiInfo, 
  FiClipboard, FiFileText, FiRefreshCw, FiExternalLink, FiClock, FiAlertCircle, FiCheckCircle
} from "react-icons/fi";

const Tickets = () => {
  const [tickets, setTickets] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("All");

  // Update States
  const [assignWorker, setAssignWorker] = useState("");
  const [newStatus, setNewStatus] = useState("");
  const [remarks, setRemarks] = useState("");

  const MEDIA_BASE_URL = "https://api.ajplservices.tech/uploads/tickets/";
  const PROFILE_BASE_URL = "https://api.ajplservices.tech/uploads/profile/";

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [tRes, wRes] = await Promise.all([
        API.get("/hod/tickets"),
        API.get("/hod/workers")
      ]);
      if (tRes.data.success) setTickets(tRes.data.data);
      if (wRes.data.success) setWorkers(wRes.data.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const filteredData = useMemo(() => {
    let data = tickets;
    if (activeTab !== "All") {
      data = data.filter(t => t.status.toLowerCase() === activeTab.toLowerCase());
    }
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      data = data.filter(t => 
        t.ticket_code.toLowerCase().includes(s) ||
        t.emp_name.toLowerCase().includes(s)
      );
    }
    return data;
  }, [tickets, activeTab, searchTerm]);

const handleUpdate = async () => {
  // 1. Pehle check karo ki selectedTicket null to nahi hai (Safety Check)
  if (!selectedTicket) return;

  // 2.  Status Change Restriction Check (Optional chaining '?' ke saath)
  if (selectedTicket?.status === "Resolved" || selectedTicket?.status === "Rejected") {
    alert("Restricted: Status cannot be changed once Resolved or Rejected.");
    return;
  }

  try {
    const res = await API.put("/hod/update-ticket", {
      ticketId: selectedTicket.id,
      workerId: assignWorker || null,
      status: newStatus,
      remarks: remarks
    });

    if (res.data.success) {
      alert("Record Updated Successfully!");
      setSelectedTicket(null); // Drawer band karne ke liye
      fetchData(); // List refresh karne ke liye
    }
  } catch (err) {
    console.error("Update Error:", err);
    alert(err.response?.data?.message || "Update Failed");
  }
};

const handleRejectWrongConcern = async () => {
  if (!selectedTicket) return;

  if (selectedTicket.status === "Resolved" || selectedTicket.status === "Rejected") {
    alert("Restricted: Status cannot be changed once Resolved or Rejected.");
    return;
  }

  const confirmReject = window.confirm(
    `Reject Ticket ${selectedTicket.ticket_code} as 'Wrong Concern Person / Department'?\n\nTicket will be marked as Rejected and employee will be notified automatically.`
  );
  if (!confirmReject) return;

  try {
    const res = await API.put("/hod/reject-wrong-concern", {
      ticketId: selectedTicket.id,
      customRemarks: remarks || ""
    });

    if (res.data.success) {
      alert(res.data.message || "Ticket Rejected: Wrong Concern Person.");
      setSelectedTicket(null);
      fetchData();
    }
  } catch (err) {
    console.error("Reject Error:", err);
    alert(err.response?.data?.message || "Failed to reject ticket");
  }
};

  return (
    <div style={styles.layout}>
      <Sidebar />
      <div style={styles.mainContent}>
        
        {/* HEADER SECTION */}
        <div style={styles.header}>
          <div>
            <h2 style={styles.title}>Incident Operations <span style={{color: '#D4AF37'}}>Terminal</span></h2>
            <p style={styles.subtitle}>Departmental monitoring and workforce dispatch system.</p>
          </div>
          <div style={styles.headerActions}>
             <div style={styles.statPill}>
                <span style={styles.statLabel}>LOAD</span>
                <span style={styles.statVal}>{tickets.length}</span>
             </div>
             <button onClick={fetchData} style={styles.refreshBtn}>
               <FiRefreshCw className={loading ? "spin" : ""} /> Sync
             </button>
          </div>
        </div>

        {/* FILTERS & SEARCH */}
        <div style={styles.controlRow}>
          <div style={styles.tabs}>
            {[
  "All",
  "Pending",
  "In Queue",
  "Work in Progress",
  "Next Week",
  "Next Month",
  "Next Quarter",
  "Resolved",
  "Rejected",
  "Hold"
].map(tab => (
              <button 
                key={tab} 
                onClick={() => setActiveTab(tab)}
                style={{
                  ...styles.tabBtn,
                  background: activeTab === tab ? "#D4AF37" : "transparent",
                  color: activeTab === tab ? "#000" : "#94A3B8",
                  fontWeight: activeTab === tab ? "700" : "500",
                }}
              >
                {tab}
              </button>
            ))}
          </div>

          <div style={styles.searchBox}>
            <FiSearch style={styles.searchIcon} />
            <input 
              placeholder="Search ID or Employee..." 
              style={styles.searchInput}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* DATA TABLE */}
        <div style={styles.tableWrapper}>
          {loading ? <div style={styles.loader}>Syncing with Database...</div> : (
            <table style={styles.table}>
              <thead>
                <tr style={styles.theadRow}>
                  <th style={styles.th}>TICKET REF</th>
                  <th style={styles.th}>EMPLOYEE DETAILS</th>
                  <th style={styles.th}>DEPARTMENT & NODE</th>
                  <th style={styles.th}>CURRENT STATUS</th>
                  <th style={styles.th}>OPERATIONS</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.map(t => (
                  <tr key={t.id} style={styles.tr}>
                    <td style={styles.td}>
                      <div style={styles.ticketIdBox}>{t.ticket_code}</div>
                      <div style={styles.dateTime}>{new Date(t.created_at).toLocaleDateString()}</div>
                    </td>
                    <td style={styles.td}>
                      <div style={styles.userColumn}>
                        <span style={styles.userNameText}>{t.emp_name}</span>
                        <span style={styles.userPhoneText}><FiPhone size={10} /> {t.emp_phone}</span>
                      </div>
                    </td>
                    <td style={styles.td}>
                      <div style={styles.deptColumn}>
                        <span style={styles.deptName}>{t.category_name}</span>
                        <span style={styles.nodeInfo}>Floor {t.floor_number} • Cabin {t.cabin_number}</span>
                      </div>
                    </td>
                    <td style={styles.td}>
  <div style={styles.deptColumn}>
    <span style={styles.deptName}>{t.category_name}</span>
    {/*  Red Badge for Table List */}
    {t.scanned_location && t.scanned_location !== "Direct Entry" ? (
      <span style={{
        color: '#EF4444', 
        fontSize: '11px', 
        fontWeight: '800', 
        display: 'flex', 
        alignItems: 'center', 
        gap: '4px',
        marginTop: '2px'
      }}>
        <FiMapPin size={10} /> {t.scanned_location.toUpperCase()} (VERIFIED)
      </span>
    ) : (
      <span style={styles.nodeInfo}>Floor {t.floor_number} • Cabin {t.cabin_number}</span>
    )}
  </div>
</td>
                    <td style={styles.td}><StatusBadge status={t.status} /></td>
                    <td style={styles.td}>
                      <button 
                        onClick={() => {
                          setSelectedTicket(t);
                          setNewStatus(t.status);
                          setAssignWorker(t.assigned_to || "");
                          setRemarks(t.remarks || "");
                        }} 
                        style={styles.viewActionBtn}
                      >
                        View Details <FiExternalLink style={{marginLeft: '6px'}}/>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* TICKET DETAIL DRAWER */}
      {selectedTicket && (
        <div style={styles.overlay} onClick={() => setSelectedTicket(null)}>
          <div style={styles.drawer} onClick={e => e.stopPropagation()}>
            <div style={styles.drawerHeader}>
              <div>
                <span style={styles.drawerSubTitle}>Incident Reference</span>
                <h3 style={styles.drawerTitle}>{selectedTicket.ticket_code}</h3>
              </div>
              <button onClick={() => setSelectedTicket(null)} style={styles.closeBtn}><FiX /></button>
            </div>

            <div style={styles.drawerBody}>
              {/* Profile Section */}
              <Section title="Requestor Profile">
                <div style={styles.pBox}>
                  <div style={styles.pAvatarWrapper}>
                    {selectedTicket.emp_image ? (
                      <img src={`${PROFILE_BASE_URL}${selectedTicket.emp_image}`} alt="emp" style={styles.pImg} />
                    ) : <div style={styles.pPlaceholder}>{selectedTicket.emp_name.charAt(0)}</div>}
                  </div>
                  <div style={{flex: 1}}>
                    <div style={styles.pName}>{selectedTicket.emp_name}</div>
                    <div style={styles.pPhone}>{selectedTicket.emp_phone}</div>
                  </div>
                  <div style={styles.pActions}>
                    <button onClick={() => window.location.href=`tel:${selectedTicket.emp_phone}`} style={styles.pCall}><FiPhone/></button>
                    <button onClick={() => window.open(`https://wa.me/${selectedTicket.emp_phone}`, '_blank')} style={styles.pWa}><FiMessageCircle/></button>
                  </div>
                </div>
              </Section>

              {/* Problem Details */}
              <Section title="Problem Classification">
                <DataRow label="Main Category" value={selectedTicket.category_name} icon={<FiClipboard/>} />
                <DataRow label="Sub-Category" value={selectedTicket.subcategory_name} icon={<FiFileText/>} />
                <DetailRow 
    label="Sub-Category Info" 
    value={selectedTicket.subcategory_info || "No technical metadata provided."} 
    italic={!selectedTicket.subcategory_info} 
    icon={<FiInfo/>} 
  />
                <DataRow label="Description" value={selectedTicket.description} isDesc />
                {/*  NEW RED VERIFIED BLOCK */}
  {selectedTicket.scanned_location && selectedTicket.scanned_location !== "Direct Entry" && (
    <div style={styles.verifiedLocationBox}>
      <div style={styles.verifiedHeader}>
        <FiCheckCircle size={12} /> QR CODE AUTHENTICATED
      </div>
      <div style={styles.verifiedBody}>
        {selectedTicket.scanned_location.toUpperCase()}
      </div>
    </div>
  )}
                <div style={styles.locBadgeDetailed}>
                    <FiMapPin size={14} /> Floor {selectedTicket.floor_number} — Cabin {selectedTicket.cabin_number}
                </div>
              </Section>

              {/* Media Section */}
              <Section title="Evidence Assets">
                <div style={styles.mediaRow}>
                   {selectedTicket.image && <MediaItem type="Image" icon={<FiImage color="#F59E0B"/>} url={MEDIA_BASE_URL+selectedTicket.image} />}
                   {selectedTicket.video && <MediaItem type="Video" icon={<FiPlayCircle color="#EF4444"/>} url={MEDIA_BASE_URL+selectedTicket.video} />}
                   {selectedTicket.audio && <MediaItem type="Audio" icon={<FiMic color="#3B82F6"/>} url={MEDIA_BASE_URL+selectedTicket.audio} />}
                   {!selectedTicket.image && !selectedTicket.video && !selectedTicket.audio && <span style={styles.noMedia}>No Attachments</span>}
                </div>
              </Section>

              {/* Workflow Section */}
              <Section title="Terminal Operations">
                <label style={styles.fLabel}>Operational Status</label>
                <select style={styles.fSelect} value={newStatus} onChange={e => setNewStatus(e.target.value)}>
                   {[
  "Pending",
  "In Queue",
  "Work in Progress",
  "Next Week",
  "Next Month",
  "Next Quarter",
  "Resolved",
  "Rejected",
  "Hold"
].map((s) => (
                     <option key={s} value={s}>{s}</option>
                    ))}
                </select>

                <label style={styles.fLabel}>Technician Assignment</label>
                <select style={styles.fSelect} value={assignWorker} onChange={e => setAssignWorker(e.target.value)}>
                   <option value="">-- Manual/Unassigned --</option>
                   {workers.map(w => (
                     <option key={w.id} value={w.id}>{w.name} [{w.active_tasks} Active]</option>
                   ))}
                </select>

                <label style={styles.fLabel}>HOD Instructions</label>
                <textarea style={styles.fArea} value={remarks} onChange={e => setRemarks(e.target.value)} placeholder="Enter technical remarks for the staff..." />
                
                <button 
                  onClick={handleUpdate} 
                  style={{
                    ...styles.saveBtn,
                    opacity: (selectedTicket?.status === "Resolved" || selectedTicket?.status === "Rejected") ? 0.5 : 1,
                    cursor: (selectedTicket?.status === "Resolved" || selectedTicket?.status === "Rejected") ? "not-allowed" : "pointer"
                  }}
                  disabled={selectedTicket?.status === "Resolved" || selectedTicket?.status === "Rejected"}
                >
                  {(selectedTicket?.status === "Resolved" || selectedTicket?.status === "Rejected") 
                    ? "Status Locked (Finalized)" 
                    : "Commit Changes & Sync"}
                </button>

                {/* 🚫 Wrong Concern Person Auto-Rejection Action */}
                {selectedTicket?.status !== "Resolved" && selectedTicket?.status !== "Rejected" && (
                  <button
                    onClick={handleRejectWrongConcern}
                    style={styles.rejectWrongBtn}
                  >
                    <FiAlertCircle size={15} />
                    Reject (Wrong Concern Person / Department)
                  </button>
                )}
              </Section>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* --- UI HELPERS --- */
const Section = ({ title, children }) => (
  <div style={{marginBottom: '25px'}}>
    <div style={styles.secLabel}>{title}</div>
    <div style={styles.secCard}>{children}</div>
  </div>
);

const DataRow = ({ label, value, icon, isDesc }) => (
  <div style={{display: 'flex', marginBottom: isDesc ? '15px' : '10px', fontSize: '13px', flexDirection: isDesc ? 'column' : 'row'}}>
    <span style={{width: isDesc ? '100%' : '120px', color: '#94A3B8', fontWeight: '500', marginBottom: isDesc ? '5px' : '0'}}>{label}</span>
    <span style={{fontWeight: '600', color: '#1E293B', flex: 1, fontSize: isDesc ? '14px' : '13px'}}>{value}</span>
  </div>
);

const MediaItem = ({ type, icon, url }) => (
  <div onClick={() => window.open(url, '_blank')} style={styles.mediaPill}>
    {icon} <span>{type}</span>
  </div>
);

const StatusBadge = ({ status }) => {
  const s = status?.toLowerCase() || 'pending';
const colors = {
  pending: {
    bg: "#FFFBEB",
    txt: "#D97706",
    ic: <FiClock />
  },

  "in queue": {
    bg: "#FFF7ED",
    txt: "#EA580C",
    ic: <FiClock />
  },

  "work in progress": {
    bg: "#ECFEFF",
    txt: "#0891B2",
    ic: <FiActivity />
  },

  "next week": {
    bg: "#EFF6FF",
    txt: "#2563EB",
    ic: <FiActivity />
  },

  "next month": {
    bg: "#F5F3FF",
    txt: "#7C3AED",
    ic: <FiActivity />
  },

  "next quarter": {
    bg: "#EEF2FF",
    txt: "#4338CA",
    ic: <FiActivity />
  },

  resolved: {
    bg: "#ECFDF5",
    txt: "#10B981",
    ic: <FiCheckCircle />
  },

  rejected: {
    bg: "#FEF2F2",
    txt: "#EF4444",
    ic: <FiAlertCircle />
  },

  hold: {
    bg: "#F1F5F9",
    txt: "#64748B",
    ic: <FiAlertCircle />
  }
};
  const c = colors[s] || colors.pending;
  return (
    <div style={{...styles.badge, background: c.bg, color: c.txt}}>
        {c.ic} <span style={{marginLeft: '6px'}}>{status}</span>
    </div>
  );
};
// component ke bahar add karein
const DetailRow = ({ label, value, icon, isDesc, italic }) => (
  <div style={{
    display: 'flex', 
    marginBottom: isDesc ? '15px' : '10px', 
    fontSize: '13px', 
    flexDirection: isDesc ? 'column' : 'row'
  }}>
    <span style={{
      width: isDesc ? '100%' : '140px', 
      color: '#94A3B8', 
      fontWeight: '600', 
      display: 'flex', 
      alignItems: 'center', 
      gap: '8px',
      marginBottom: isDesc ? '5px' : '0'
    }}>
      {icon} {label}
    </span>
    <span style={{
      fontWeight: '700', 
      color: '#1E293B', 
      flex: 1, 
      fontSize: isDesc ? '14px' : '13.5px',
      fontStyle: italic ? 'italic' : 'normal'
    }}>
      {value || "---"}
    </span>
  </div>
);

/* --- DESIGN SYSTEM STYLES --- */
const styles = {
  layout: { display: "flex", background: "#F8FAFC", minHeight: "100vh" },
  mainContent: { marginLeft: "260px", padding: "30px 40px", flex: 1, fontFamily: "'Inter', sans-serif" },
  
  header: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "35px" },
  title: { margin: 0, fontSize: "28px", fontWeight: "800", color: "#1E293B", letterSpacing: '-0.5px' },
  subtitle: { margin: 0, color: "#94A3B8", fontSize: "14px" },
  headerActions: { display: 'flex', gap: '20px', alignItems: 'center' },
  statPill: { background: '#FFF', padding: '10px 20px', borderRadius: '15px', border: '1px solid #E2E8F0', textAlign: 'center', minWidth: '100px' },
  statLabel: { display: 'block', fontSize: '10px', color: '#94A3B8', fontWeight: '800' },
  statVal: { fontSize: '20px', fontWeight: '900', color: '#D4AF37' },
  refreshBtn: { display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 22px', borderRadius: '14px', border: '1px solid #E2E8F0', background: '#FFF', fontWeight: '700', cursor: 'pointer', transition: '0.3s' },

  controlRow: { display: 'flex', justifyContent: 'space-between', marginBottom: '25px', gap: '20px', alignItems: 'center' },
  tabs: { display: 'flex', gap: '8px', background: '#F1F5F9', padding: '5px', borderRadius: '14px' },
  tabBtn: { border: 'none', padding: '10px 18px', borderRadius: '10px', fontSize: '13px', cursor: 'pointer', transition: '0.2s' },
  
  searchBox: { position: 'relative', flex: 1, maxWidth: '400px' },
  searchIcon: { position: 'absolute', left: '15px', top: '50%', transform: 'translateY(-50%)', color: '#94A3B8' },
  searchInput: { width: '100%', padding: '12px 15px 12px 45px', borderRadius: '14px', border: '1px solid #E2E8F0', outline: 'none', background: '#FFF', fontSize: '14px' },

  tableWrapper: { background: "#FFF", borderRadius: "24px", padding: "10px", boxShadow: "0 10px 30px rgba(0,0,0,0.03)", border: '1px solid #F1F5F9' },
  table: { width: "100%", borderCollapse: "separate", borderSpacing: '0 10px' },
  th: { padding: "15px 20px", textAlign: 'left', fontSize: '11px', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '1px' },
  tr: { transition: '0.2s' },
  td: { padding: "18px 20px", verticalAlign: 'middle', borderBottom: '1px solid #F8FAFC' },

  ticketIdBox: { background: "#D4AF3715", color: "#D4AF37", padding: "6px 12px", borderRadius: "10px", fontWeight: "800", fontSize: '12px', display: 'inline-block' },
  dateTime: { fontSize: '10px', color: '#CBD5E1', marginTop: '5px', fontWeight: '600' },
  
  userColumn: { display: 'flex', flexDirection: 'column', gap: '2px' },
  userNameText: { fontWeight: '700', color: '#1E293B', fontSize: '15px' },
  userPhoneText: { fontSize: '12px', color: '#94A3B8', display: 'flex', alignItems: 'center', gap: '5px' },

  deptColumn: { display: 'flex', flexDirection: 'column' },
  deptName: { fontWeight: '700', color: '#475569', fontSize: '14px' },
  nodeInfo: { fontSize: '11px', color: '#94A3B8', fontWeight: '500' },

  badge: { padding: '8px 14px', borderRadius: '12px', fontSize: '11px', fontWeight: '800', display: 'flex', alignItems: 'center', width: 'fit-content' },
  viewActionBtn: { background: "#1E293B", color: "#FFF", border: "none", padding: '10px 20px', borderRadius: '12px', cursor: 'pointer', fontWeight: '700', display: 'flex', alignItems: 'center', fontSize: '12px' },

  /* DRAWER STYLES */
  overlay: { position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(15, 23, 42, 0.4)", zIndex: 1000, display: "flex", justifyContent: "flex-end", backdropFilter: 'blur(6px)' },
  drawer: { width: "520px", height: "100%", background: "#F8FAFC", boxShadow: "-20px 0 60px rgba(0,0,0,0.15)", display: "flex", flexDirection: "column" },
  drawerHeader: { padding: "30px", borderBottom: "1px solid #E2E8F0", display: "flex", justifyContent: "space-between", alignItems: "center", background: '#FFF' },
  drawerSubTitle: { fontSize: '10px', color: '#D4AF37', fontWeight: '900', textTransform: 'uppercase' },
  drawerTitle: { margin: 0, fontSize: '22px', fontWeight: '800', color: '#1E293B' },
  closeBtn: { background: "#F1F5F9", border: "none", width: '40px', height: '40px', borderRadius: '50%', cursor: "pointer", display: 'flex', alignItems: 'center', justifyContent: 'center' },
  drawerBody: { flex: 1, overflowY: "auto", padding: "25px 30px" },

  secLabel: { fontSize: '11px', color: '#94A3B8', fontWeight: '900', textTransform: 'uppercase', marginBottom: '10px', letterSpacing: '1px' },
  secCard: { background: '#FFF', padding: '20px', borderRadius: '20px', border: '1px solid #F1F5F9' },

  pBox: { display: 'flex', alignItems: 'center', gap: '15px' },
  pAvatarWrapper: { width: '60px', height: '60px', borderRadius: '18px', overflow: 'hidden', border: '2px solid #F1F5F9' },
  pImg: { width: '100%', height: '100%', objectFit: 'cover' },
  pPlaceholder: { width: '100%', height: '100%', background: '#D4AF37', color: '#FFF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '900', fontSize: '24px' },
  pName: { fontWeight: '800', fontSize: '17px', color: '#1E293B' },
  pPhone: { fontSize: '13px', color: '#94A3B8' },
  pActions: { display: 'flex', gap: '10px' },
  pCall: { background: '#ECFDF5', color: '#10B981', border: 'none', padding: '12px', borderRadius: '12px', cursor: 'pointer' },
  pWa: { background: '#E6FFFA', color: '#25D366', border: 'none', padding: '12px', borderRadius: '12px', cursor: 'pointer' },

  locBadgeDetailed: { background: '#F8FAFC', padding: '12px', borderRadius: '12px', marginTop: '10px', color: '#64748B', fontSize: '13px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '10px' },

  mediaRow: { display: 'flex', gap: '12px' },
  mediaPill: { flex: 1, background: '#F8FAFC', border: '1px solid #E2E8F0', padding: '12px', borderRadius: '15px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontWeight: '700', fontSize: '12px' },
  noMedia: { color: '#CBD5E1', fontSize: '12px', fontStyle: 'italic' },

  /* --- ADD THESE TO YOUR styles OBJECT --- */
verifiedLocationBox: {
  background: '#FEF2F2', 
  border: '1px solid #FEE2E2', 
  borderRadius: '15px', 
  padding: '15px', 
  marginTop: '15px',
  display: 'flex',
  flexDirection: 'column',
  gap: '5px'
},
verifiedHeader: {
  fontSize: '10px', 
  fontWeight: '900', 
  color: '#EF4444', 
  display: 'flex', 
  alignItems: 'center', 
  gap: '6px',
  letterSpacing: '1px'
},
verifiedBody: {
  fontSize: '16px', 
  fontWeight: '900', 
  color: '#991B1B', // Dark Red
  fontFamily: "'Space Grotesk', sans-serif"
},

  fLabel: { display: "block", fontSize: "11px", fontWeight: "800", marginTop: "15px", marginBottom: "5px", color: '#64748B', textTransform: 'uppercase' },
  fSelect: { width: '100%', padding: '14px', borderRadius: '12px', border: '1px solid #E2E8F0', outline: 'none', background: '#FFF', fontSize: '14px', fontWeight: '600' },
  fArea: { width: '100%', height: '100px', padding: '14px', borderRadius: '12px', border: '1px solid #E2E8F0', outline: 'none', resize: 'none', fontSize: '14px' },
  saveBtn: { width: '100%', padding: '18px', background: '#D4AF37', color: '#000', border: 'none', borderRadius: '18px', fontWeight: '900', marginTop: '25px', cursor: 'pointer', boxShadow: '0 10px 25px rgba(212, 175, 55, 0.25)', transition: '0.3s' },
  rejectWrongBtn: { width: '100%', padding: '14px', background: '#FEF2F2', color: '#DC2626', border: '1.5px solid #FECACA', borderRadius: '16px', fontWeight: '800', marginTop: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontSize: '13px', transition: '0.2s' },
  
  loader: { padding: '50px', textAlign: 'center', color: '#94A3B8', fontWeight: '600' }
};

export default Tickets;